<script setup>
import {computed, ref} from "vue";
import {createCSV, download} from "@/helpers/file";
import SortableColumnHeader from "@/components/ui/SortableColumnHeader.vue";
import VClamp from "vue-clamp";
import {getNumberWithNoun} from "@/helpers/string";
import AddStartupsCustomField from "@/components/startups/import/AddStartupsCustomField.vue";
import AddStartupsUrl from "@/components/startups/import/AddStartupsUrl.vue";

const emit = defineEmits(['apply']);

const showModal = ref(false);
const formData = ref([]);
const columns = ref([]);

const sort = ref({
    field: '',
    dir: ''
});

const noUrlCount = computed(() => {
    return formData.value.filter(row => !row.url).length;
});

const selectedAll = computed({
    get: () => {
        return formData.value.every(row => row.selected);
    },
    set: (value) => {
        formData.value.forEach(row => {
            row.selected = value;
        });
    }
});

const importedData = computed(() => {
    return formData.value.filter(item => item.selected)
        .map(item => Object.fromEntries(columns.value.map(col => [col.name, item[col.name]])));
});

function downloadFile() {
    download(createCSV(importedData.value), 'startup.csv');
}

function changeSort(newSort) {
    sort.value = newSort;

    if (sort.value.field) {
        formData.value.sort((a, b) => {
            if (a[sort.value.field] < b[sort.value.field]) {
                return sort.value.dir === 'asc' ? -1 : 1;
            }
            if (a[sort.value.field] > b[sort.value.field]) {
                return sort.value.dir === 'asc' ? 1 : -1;
            }

            return 0;
        });
    }
}

function getActualOptions(fieldName, row) {
    const fieldData = columns.value.find(column => column.name === fieldName).field_data;
    const options = fieldData.options.map(option => option.value);

    if (!fieldData.depends_on) {
        if (fieldData.type === 'users_list') {
            return fieldData.options.map(option => option.email);
        }

        return options;
    }

    const mainFieldColumn = columns.value.find(column => column.field_data?.id === fieldData.depends_on);

    if (!mainFieldColumn) {
        return options;
    }

    const mainFieldData = mainFieldColumn.field_data;
    const selectedMainOption = row[mainFieldColumn.name];

    if (!selectedMainOption) {
        return [];
    }

    const selectedMainOptionId = mainFieldData.options.find(option => option.value === selectedMainOption).id;

    return fieldData.options.filter(option => option.depends_on === selectedMainOptionId).map(option => option.value);
}

function customFieldChanged(column, row) {
    const dependentFields = columns.value.filter(item => column.field_data.id === item.field_data?.depends_on);

    dependentFields.forEach(field => {
        row[field.name + '_raw'] = row[field.name];
        row[field.name] = '';

        customFieldChanged(field, row);
    });
}

async function apply() {
    showModal.value = false;
    emit('apply', importedData.value);
}

function open(data) {
    columns.value = data['columns'];

    formData.value = data['data'].map(item => ({
        selected: true,
        ...item
    }));

    showModal.value = true;
}

defineExpose({
    open
});
</script>

<template>
    <Modal v-model="showModal" modal-class="modal-w-xxxxl modal-auto-tagging" :title="`${formData.length} startups`">
        <div style="overflow: auto" class="main-table-wrapper">
            <table class="modal-auto-tagging__table">
                <thead>
                    <tr>
                        <th>
                            <div class="input-checkbox">
                                <input id="modal-input-checkbox__all" type="checkbox" v-model="selectedAll">
                                <label for="modal-input-checkbox__all"></label>
                                <!-- <div>-</div> -->
                            </div>
                        </th>

                        <SortableColumnHeader
                            v-for="(column, index) in columns"
                            :key="index"
                            :column="column"
                            :sort="sort"
                            :class="{'wide-column': column.name === 'description'}"
                            @changeSort="changeSort"
                        />
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        <td colspan="100%" class="modal-auto-tagging__table__top">
                            <div v-if="noUrlCount" class="modal-auto-tagging__label">
                                {{getNumberWithNoun('startup', noUrlCount)}} without URL
                            </div>
                        </td>
                    </tr>
                    <tr v-for="(row, rowIndex) in formData" :key="rowIndex">
                        <td>
                            <div class="input-checkbox">
                                <input :id="'modal-checkbox-' + rowIndex" type="checkbox" v-model="formData[rowIndex].selected">
                                <label :for="'modal-checkbox-' + rowIndex"></label>
                            </div>
                        </td>

                        <td v-for="(column, colIndex) in columns" :key="colIndex">
                            <a v-if="column.name === 'company' && row.startup_id" :href="`/startups/${row.startup_id}`" target="_blank">{{ row[column.name] }}</a>

                            <template v-else-if="column.name === 'url'">
                                <a v-if="row[column.name]" :href="row[column.name]" target="_blank">{{ row[column.name] }}</a>

                                <AddStartupsUrl
                                    v-else
                                    v-model="formData[rowIndex][column.name]"
                                />
                            </template>

                            <AddStartupsCustomField
                                v-else-if="column.custom"
                                v-model="formData[rowIndex][column.name]"
                                :fieldData="column.field_data"
                                :actualOptions="getActualOptions(column.name, row)"
                                :raw-value="formData[rowIndex][column.name + '_raw']"
                                @changed="() => customFieldChanged(column, row)"
                            />

                            <VClamp v-else
                                autoresize :max-lines="3"
                            >
                                {{ row[column.name] }}

                                <template #after="{ toggle, expanded, clamped }">
                                    <a class="link-normal" @click="toggle">
                                        <template v-if="clamped">
                                            more
                                        </template>
                                        <template v-if="expanded">
                                            less
                                        </template>
                                    </a>
                                </template>
                            </VClamp>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <template #modal-footer>
            <!-- <div style="display: flex; gap: 15px;"> -->
                <b-button @click="downloadFile" class="link link--download">Download CSV</b-button>
                <b-button variant="primary" @click="apply" class="btn-lg">Add Selected & Close</b-button>
            <!-- </div> -->
        </template>
    </Modal>
</template>

<style scoped>
    .wide-column {
        min-width: 300px;
    }
</style>
